// src/services/categorizationService.js - Modificado para suportar configuração de categorização 
import { matchTransactionPattern, extractBoletoInfo, normalizeSicoobDescription } from '../utils/patternRules';

/**
 * Serviço para lidar com as operações de categorização automática
 */

/**
 * Verifica se estamos usando uma conta pessoal
 * @returns {boolean} - True se a conta ativa for do tipo pessoal
 */
const isPersonalAccount = () => {
  try {
    return localStorage.getItem('active_account_type') === 'personal';
  } catch (error) {
    console.error('Erro ao verificar tipo de conta:', error);
    return false;
  }
};

/**
 * Verifica se estamos usando uma conta empresarial
 * @returns {boolean} - True se a conta ativa for do tipo empresarial
 */
const isBusinessAccount = () => {
  try {
    return localStorage.getItem('active_account_type') === 'business';
  } catch (error) {
    console.error('Erro ao verificar tipo de conta:', error);
    return true; // Por padrão, assumimos conta empresarial
  }
};

/**
 * Verifica se a categorização automática para bancos está ativada
 * @returns {boolean} - True se a categorização automática estiver ativada
 */
const isAutomaticBankCategorizationEnabled = () => {
  try {
    return localStorage.getItem('automaticBankCategorizationOFX') !== 'false';
  } catch (error) {
    console.error('Erro ao verificar configuração de categorização automática:', error);
    return true; // Por padrão, está ativada
  }
};

/**
 * Cria uma chave para categorização baseada no beneficiário do boleto
 * @param {string} beneficiaryName - Nome do beneficiário
 * @returns {string} - Chave normalizada
 */
const createBeneficiaryKey = (beneficiaryName) => {
  if (!beneficiaryName) return null;
  return `beneficiary_${beneficiaryName.trim().toLowerCase()}`;
};

/**
 * Verifica se uma transação deve ser automaticamente categorizada
 * @param {Object} transaction - Dados da transação
 * @param {Object} categoryMappings - Mapeamentos de categorias do usuário
 * @returns {Object} - Resultado contendo se está categorizada e informações da categoria
 */
export const checkTransactionCategorization = (transaction, categoryMappings) => {
  if (!transaction || !categoryMappings) {
    return { isCategorized: false };
  }
  
  const bankName = transaction.bankInfo?.org || "";
  const amount = transaction.amount || 0;
  
  // Nova lógica para contas empresariais com receitas (valores positivos)
  const isBusinessAcc = isBusinessAccount();
  const isAutomaticCategorizationEnabled = isAutomaticBankCategorizationEnabled();
  
  // Para conta empresarial, verificar se a categorização automática está desativada
  if (isBusinessAcc && amount > 0 && !isAutomaticCategorizationEnabled) {
    // Se for conta empresarial e a categorização automática estiver desativada,
    // categorizar diretamente como RECEITA.Receitas para qualquer valor positivo
    return {
      isCategorized: true,
      mainCategory: "RECEITA",
      subCategory: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      source: "business_default_income_rule"
    };
  }
  
  // Verificar se estamos em uma conta pessoal e se é uma transação positiva
  if (isPersonalAccount() && amount > 0) {
    return {
      isCategorized: true,
      mainCategory: "💸 Receitas",
      subCategory: "Receitas",
      categoryPath: "💸 Receitas.Receitas",
      autoMapped: true,
      source: "personal_account_rule"
    };
  }
  
  // Verificar se é uma transação do SICOOB
  const isSicoob = 
    bankName.includes("SICOOB") || 
    bankName.includes("COOP DE CRED") ||
    transaction.description.toUpperCase().includes("SICOOB") || 
    transaction.description.toUpperCase().includes("SICREDI");
  
  // Aplicar normalização especial para SICOOB/SICREDI
  // Esta normalização é para encontrar um mapeamento existente e para exibição.
  // A lógica de agrupamento de PIX genérico SICOOB será tratada mais adiante
  // ao criar/usar as chaves de mapeamento.
  const normalizedDescriptionForLookup = isSicoob ? 
    normalizeSicoobDescription(transaction.description, bankName) : 
    transaction.description.trim().toLowerCase();
  
  // Adicionamos um prefixo baseado no sinal do valor
  const signPrefix = amount > 0 ? "positive_" : "negative_";
  const signedKey = signPrefix + normalizedDescriptionForLookup;
  
  // Estrutura de resposta padrão
  const result = {
    isCategorized: false,
    mainCategory: null,
    subCategory: null,
    categoryPath: null,
    autoMapped: false,
    source: null
  };
  
  // 1. Verificação especial para transações SICOOB
  if (isSicoob) {
    // Para lookup, tentamos a chave normalizada e a chave genérica para PIX recebido.
    let sicoobLookupKeyPart = normalizedDescriptionForLookup;
    const descriptionUpperCase = transaction.description.toUpperCase();
    const isReceita = amount > 0;
    const isGenericPixReceipt = isReceita &&
                                (descriptionUpperCase.includes("RECEBIMENTO PIX-PIX CRED") || descriptionUpperCase.includes("RECEBIMENTO PIX")) &&
                                !['ALELO', 'IFOOD', 'BANCO VR', 'PLUXEE', 'SAFE2PAY'].some(kw => descriptionUpperCase.includes(kw));

    if (isGenericPixReceipt) {
      // Se for um PIX genérico, o mapeamento pode estar salvo com a chave genérica.
      sicoobLookupKeyPart = "recebimento pix generico sicoob";
    }
    
    const sicoobKeyToFind = `sicoob_${sicoobLookupKeyPart}`;
    const specificSicoobKey = `sicoob_${normalizedDescriptionForLookup}`; // Chave com o nome do pagador, caso exista um mapeamento específico.

    // Tenta primeiro a chave específica (com nome do pagador) se não for genérica
    // Ou se for um mapeamento de despesa, onde a individualização pode ser desejada.
    let mappingToUse = null;
    if (!isGenericPixReceipt && categoryMappings[specificSicoobKey]) {
        mappingToUse = categoryMappings[specificSicoobKey];
    } else if (categoryMappings[sicoobKeyToFind]) { // Tenta a chave (potencialmente genérica)
        mappingToUse = categoryMappings[sicoobKeyToFind];
    } else if (isGenericPixReceipt && categoryMappings[specificSicoobKey]) {
        // Fallback: se era pra ser genérico mas não achou, e existe um específico, usa o específico.
        // Isso pode acontecer se um mapeamento específico foi criado antes da lógica de generalização.
        mappingToUse = categoryMappings[specificSicoobKey];
    }

    if (mappingToUse) {
      const mapping = mappingToUse;
      
      if ((mapping.positiveOnly && amount <= 0) || 
          (mapping.negativeOnly && amount > 0)) {
        console.log(`Transação SICOOB normalizada "${normalizedDescriptionForLookup}" (chave de busca: "${sicoobKeyToFind}") ignorada devido à incompatibilidade de sinal`);
      } else {
        result.isCategorized = true;
        result.mainCategory = mapping.groupName;
        result.subCategory = mapping.categoryName;
        result.categoryPath = mapping.categoryPath;
        result.source = "sicoob_normalized_mapping";
        return result;
      }
    }
  }
  
  // 2. Verificar se é um boleto e tentar categorizar pelo beneficiário
  const boletoInfo = extractBoletoInfo(transaction.description);
  if (boletoInfo && boletoInfo.beneficiary) {
    const beneficiaryKey = createBeneficiaryKey(boletoInfo.beneficiary);
    
    if (beneficiaryKey && categoryMappings[beneficiaryKey]) {
      const mapping = categoryMappings[beneficiaryKey];
      
      if ((mapping.positiveOnly && amount <= 0) || 
          (mapping.negativeOnly && amount > 0)) {
        console.log(`Transação com beneficiário "${boletoInfo.beneficiary}" ignorada devido à incompatibilidade de sinal`);
      } else {
        result.isCategorized = true;
        result.mainCategory = mapping.groupName;
        result.subCategory = mapping.categoryName;
        result.categoryPath = mapping.categoryPath;
        result.source = "beneficiary_mapping";
        result.beneficiary = boletoInfo.beneficiary;
        return result;
      }
    }
  }
  
  // 3. Verificar se existe um mapeamento específico por ID de transação
  const specificMappingKeys = Object.keys(categoryMappings).filter(
    key => categoryMappings[key].isSpecificMapping && 
           categoryMappings[key].transactionId === transaction.id
  );
  
  if (specificMappingKeys.length > 0) {
    const mapping = categoryMappings[specificMappingKeys[0]];
    
    if (mapping.positiveOnly && amount <= 0) {
      console.log(`Transação ${transaction.id} ignorada devido à flag positiveOnly`);
      return result;
    }
    
    result.isCategorized = true;
    result.mainCategory = mapping.groupName;
    result.subCategory = mapping.categoryName;
    result.categoryPath = mapping.categoryPath;
    result.source = "specific_mapping";
    
    if (boletoInfo && boletoInfo.beneficiary) {
      result.beneficiary = boletoInfo.beneficiary;
    }
    
    return result;
  }
  
  // 4. Verificar se existe um mapeamento pelo signedKey (com prefixo de sinal)
  if (categoryMappings[signedKey]) {
    const mapping = categoryMappings[signedKey];
    
    if (!mapping.isSpecificMapping && 
        (!mapping.bankInfo || !mapping.bankInfo.org || 
         bankName.includes(mapping.bankInfo.org))) {
      
      if (mapping.positiveOnly && amount <= 0) {
        console.log(`Transação com descrição "${normalizedDescriptionForLookup}" ignorada devido à flag positiveOnly`);
        return result;
      }
      
      result.isCategorized = true;
      result.mainCategory = mapping.groupName;
      result.subCategory = mapping.categoryName;
      result.categoryPath = mapping.categoryPath;
      result.source = "direct_mapping_signed";
      return result;
    }
  }
  
  // 5. Verificar mapeamento direto (forma legada, manter para compatibilidade)
  if (categoryMappings[normalizedDescriptionForLookup]) {
    const mapping = categoryMappings[normalizedDescriptionForLookup];
    
    if (!mapping.isSpecificMapping && 
        (!mapping.bankInfo || !mapping.bankInfo.org || 
         bankName.includes(mapping.bankInfo.org))) {
      
      if (mapping.positiveOnly && amount <= 0) {
        console.log(`Transação com descrição "${normalizedDescriptionForLookup}" ignorada devido à flag positiveOnly em mapeamento legado`);
        return result;
      }
      
      if (mapping.categoryPath && mapping.categoryPath.startsWith("RECEITA.") && amount <= 0) {
        console.log(`Transação com descrição "${normalizedDescriptionForLookup}" ignorada: categoria RECEITA aplicada a valor negativo`);
        return result;
      }
      
      result.isCategorized = true;
      result.mainCategory = mapping.groupName;
      result.subCategory = mapping.categoryName;
      result.categoryPath = mapping.categoryPath;
      result.source = "direct_mapping_legacy";
      return result;
    }
  }
  
  // 6. Verificar se corresponde a algum padrão
  const patternMatch = matchTransactionPattern(normalizedDescriptionForLookup, bankName, amount, categoryMappings);
  
  if (patternMatch && categoryMappings[patternMatch.patternKey]) {
    const mapping = categoryMappings[patternMatch.patternKey];
    
    if (mapping.positiveOnly && amount <= 0) {
      console.log(`Transação com padrão "${patternMatch.patternKey}" ignorada devido à flag positiveOnly`);
      return result;
    }
    
    if (mapping.categoryPath && mapping.categoryPath.startsWith("RECEITA.") && amount <= 0) {
      console.log(`Transação com padrão "${patternMatch.patternKey}" ignorada: categoria RECEITA aplicada a valor negativo`);
      return result;
    }
    
    result.isCategorized = true;
    result.mainCategory = mapping.groupName;
    result.subCategory = mapping.categoryName;
    result.categoryPath = mapping.categoryPath;
    result.patternKey = patternMatch.patternKey;
    result.source = "pattern_mapping";
    return result;
  }
  
  // 7. Verificar se foi previamente auto-categorizada durante parsing
  if (transaction.autoMapped && transaction.category && transaction.categoryPath) {
    if (transaction.categoryPath.startsWith("RECEITA.") && amount <= 0) {
      console.log(`Transação auto-mapeada ignorada: categoria RECEITA aplicada a valor negativo`);
      return result;
    }
    
    result.isCategorized = true;
    result.mainCategory = transaction.categoryPath.split('.')[0];
    result.subCategory = transaction.category;
    result.categoryPath = transaction.categoryPath;
    result.autoMapped = true;
    result.source = "auto_mapped";
    return result;
  }
  
  // 8. NOVO: Fallback para receitas em contas empresariais
  // Se chegou até aqui, nenhuma regra se aplicou, mas se for uma conta empresarial e uma receita,
  // podemos aplicar o fallback para "RECEITA.Receitas"
  if (isBusinessAccount() && amount > 0) {
    return {
      isCategorized: true,
      mainCategory: "RECEITA",
      subCategory: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      source: "business_default_income_fallback"
    };
  }
  
  return result;
};

/**
 * Identifica padrões de categorização para uma transação
 * @param {Object} transaction - Dados da transação
 * @returns {Object|null} - Informações do padrão ou null
 */
export const identifyTransactionPattern = (transaction) => {
  if (!transaction || !transaction.description || !transaction.bankInfo?.org) {
    return null;
  }
  
  // Verificar se estamos em uma conta pessoal e o valor é positivo
  if (isPersonalAccount() && transaction.amount > 0) {
    return {
      patternKey: `personal_account_${transaction.id}`,
      category: "Receitas",
      categoryPath: "💸 Receitas.Receitas",
      autoMapped: true,
      positiveOnly: true
    };
  }
  
  // Verificar se estamos em uma conta empresarial com a categorização automática desativada
  if (isBusinessAccount() && transaction.amount > 0 && !isAutomaticBankCategorizationEnabled()) {
    return {
      patternKey: `business_default_${transaction.id}`,
      category: "Receitas",
      categoryPath: "RECEITA.Receitas",
      autoMapped: true,
      positiveOnly: true
    };
  }
  
  const bankName = transaction.bankInfo.org;
  const amount = transaction.amount || 0;
  const descriptionUpperCase = transaction.description.toUpperCase(); // Original description for checks

  const isSicoob = 
    bankName.includes("SICOOB") || 
    bankName.includes("COOP DE CRED") ||
    descriptionUpperCase.includes("SICOOB") || 
    descriptionUpperCase.includes("SICREDI");
    
  if (isSicoob) {
    const normalizedDescForDisplay = normalizeSicoobDescription(transaction.description, bankName);
    let keyPart = normalizedDescForDisplay;

    // Lógica de generalização para receitas PIX SICOOB genéricas
    const isReceita = amount > 0;
    const isGenericPixReceipt = isReceita &&
                                (descriptionUpperCase.includes("RECEBIMENTO PIX-PIX CRED") || descriptionUpperCase.includes("RECEBIMENTO PIX")) &&
                                !['ALELO', 'IFOOD', 'BANCO VR', 'PLUXEE', 'SAFE2PAY'].some(kw => descriptionUpperCase.includes(kw));

    if (isGenericPixReceipt) {
      keyPart = "recebimento pix generico sicoob";
    }

    return {
      patternKey: `sicoob_${keyPart}`, // patternKey usará a parte generalizada se aplicável
      normalizedDescription: normalizedDescForDisplay, // Para exibição, mantém a normalização padrão
      isSicoobPattern: true,
      isGenericSicoobPixReceipt: isGenericPixReceipt // Flag para ajudar a lógica de salvamento
    };
  }
  
  const boletoInfo = extractBoletoInfo(transaction.description);
  if (boletoInfo && boletoInfo.beneficiary) {
    return {
      patternKey: createBeneficiaryKey(boletoInfo.beneficiary),
      beneficiary: boletoInfo.beneficiary,
      isBeneficiaryPattern: true
    };
  }
  
  const normalizedDescription = transaction.description.trim().toLowerCase();
  return matchTransactionPattern(normalizedDescription, bankName, amount);
};

/**
 * Agrupa transações por padrões para salvar mapeamentos
 * @param {Array} transactions - Lista de transações
 * @returns {Object} - Grupos de transações por padrão
 */
export const groupTransactionsByPattern = (transactions) => {
  if (!transactions || !transactions.length) {
    return {};
  }
  
  const patternGroups = {};
  const beneficiaryGroups = {}; 
  const sicoobGroups = {}; 
  const personalAccountGroups = {};
  
  transactions.forEach(transaction => {
    const bankName = transaction.bankInfo?.org || "";
    const amount = transaction.amount || 0;
    const descriptionUpperCase = transaction.description.toUpperCase(); // Original description
    
    // Verificar se estamos em uma conta pessoal e se é uma transação positiva
    if (isPersonalAccount() && amount > 0) {
      const personalKey = `personal_account_${transaction.id}`;
      
      if (!personalAccountGroups[personalKey]) {
        personalAccountGroups[personalKey] = {
          transactions: [],
          category: "Receitas",
          categoryPath: "💸 Receitas.Receitas",
          groupName: "💸 Receitas",
          isPersonalAccountPattern: true,
          positiveOnly: true,
          amount: amount
        };
      }
      
      personalAccountGroups[personalKey].transactions.push(transaction);
      return;
    }
    
    // Verificar se estamos em uma conta empresarial com categorização automática desativada
    if (isBusinessAccount() && amount > 0 && !isAutomaticBankCategorizationEnabled()) {
      const businessKey = `business_default_${transaction.id}`;
      
      if (!personalAccountGroups[businessKey]) {
        personalAccountGroups[businessKey] = {
          transactions: [],
          category: "Receitas",
          categoryPath: "RECEITA.Receitas",
          groupName: "RECEITA",
          isBusinessDefaultPattern: true,
          positiveOnly: true,
          amount: amount
        };
      }
      
      personalAccountGroups[businessKey].transactions.push(transaction);
      return;
    }
    
    const isSicoob = 
      bankName.includes("SICOOB") || 
      bankName.includes("COOP DE CRED") ||
      descriptionUpperCase.includes("SICOOB") || 
      descriptionUpperCase.includes("SICREDI");
    
    if (isSicoob) {
      const normalizedDescForDisplay = normalizeSicoobDescription(transaction.description, bankName);
      let sicoobKeyPart = normalizedDescForDisplay; // Por padrão, usa a normalização completa

      // NOVA LÓGICA: Generalizar a chave de agrupamento para receitas PIX SICOOB genéricas
      const isReceita = amount > 0;
      // Certifique-se que transaction.categoryPath existe e é string antes de chamar startsWith
      const isReceitaCategory = transaction.categoryPath && typeof transaction.categoryPath === 'string' && transaction.categoryPath.startsWith("RECEITA.");

      const isGenericPixReceipt = (isReceita || isReceitaCategory) && // Considera tanto valor quanto categoria de receita
                                (descriptionUpperCase.includes("RECEBIMENTO PIX-PIX CRED") || descriptionUpperCase.includes("RECEBIMENTO PIX")) &&
                                !['ALELO', 'IFOOD', 'BANCO VR', 'PLUXEE', 'SAFE2PAY' /* Adicione outros aqui */].some(kw => descriptionUpperCase.includes(kw));
      
      if (isGenericPixReceipt) {
        sicoobKeyPart = "recebimento pix generico sicoob"; // Chave padronizada para agrupamento
      }
      // FIM DA NOVA LÓGICA

      const sicoobKey = `sicoob_${sicoobKeyPart}`;
      
      if (!sicoobGroups[sicoobKey]) {
        sicoobGroups[sicoobKey] = {
          transactions: [],
          category: transaction.category,
          categoryPath: transaction.categoryPath,
          groupName: transaction.groupName || transaction.categoryPath?.split('.')[0],
          bankInfo: transaction.bankInfo,
          normalizedDescription: normalizedDescForDisplay, // Para exibição
          sicoobKeyPartForGrouping: sicoobKeyPart, // Para a chave do Firebase
          isSicoobPattern: true,
          isGenericSicoobPixReceipt: isGenericPixReceipt,
          // positiveOnly/negativeOnly devem ser baseados na categoria/valor real
          positiveOnly: amount > 0 && transaction.categoryPath && transaction.categoryPath.startsWith("RECEITA."),
          negativeOnly: amount < 0 && transaction.categoryPath && transaction.categoryPath.startsWith("(-) "),
          amount: amount // Mantém o valor para verificações posteriores
        };
      }
      
      sicoobGroups[sicoobKey].transactions.push(transaction);
      // Ajusta a categoria e o caminho se for um PIX genérico e ainda não estiver definido corretamente
      if (isGenericPixReceipt && sicoobGroups[sicoobKey].categoryPath !== "RECEITA.Pix") {
          sicoobGroups[sicoobKey].category = "Pix";
          sicoobGroups[sicoobKey].categoryPath = "RECEITA.Pix";
          sicoobGroups[sicoobKey].groupName = "RECEITA";
      }
      return; 
    }
    
    const boletoInfo = extractBoletoInfo(transaction.description);
    
    if (boletoInfo && boletoInfo.beneficiary) {
      const beneficiaryKey = createBeneficiaryKey(boletoInfo.beneficiary);
      
      if (!beneficiaryGroups[beneficiaryKey]) {
        beneficiaryGroups[beneficiaryKey] = {
          transactions: [],
          category: transaction.category,
          categoryPath: transaction.categoryPath,
          groupName: transaction.groupName || transaction.categoryPath?.split('.')[0],
          bankInfo: transaction.bankInfo,
          beneficiary: boletoInfo.beneficiary,
          isBeneficiaryPattern: true,
          positiveOnly: amount > 0 && transaction.categoryPath && transaction.categoryPath.startsWith("RECEITA."),
          negativeOnly: amount < 0 && transaction.categoryPath && transaction.categoryPath.startsWith("(-) "),
          amount: amount
        };
      }
      
      beneficiaryGroups[beneficiaryKey].transactions.push(transaction);
      return;
    }
    
    const normalizedDescription = transaction.description.trim().toLowerCase();
    const signPrefix = amount > 0 ? "positive_" : "negative_";
    const signedKey = signPrefix + normalizedDescription;
    
    const pattern = matchTransactionPattern(normalizedDescription, bankName, amount);
    
    if (pattern) {
      const patternKeyWithSign = signPrefix + pattern.patternKey;
      
      if (!patternGroups[patternKeyWithSign]) {
        patternGroups[patternKeyWithSign] = {
          transactions: [],
          category: transaction.category || pattern.category,
          categoryPath: transaction.categoryPath || pattern.categoryPath,
          groupName: transaction.groupName || (transaction.categoryPath?.split('.')[0] || pattern.categoryPath?.split('.')[0]),
          bankInfo: transaction.bankInfo,
          patternKey: pattern.patternKey,
          originalPatternKey: pattern.patternKey,
          isPatternRule: true,
          positiveOnly: pattern.categoryPath && pattern.categoryPath.startsWith("RECEITA."),
          amount: amount
        };
      }
      
      patternGroups[patternKeyWithSign].transactions.push(transaction);
    } else {
      if (!patternGroups[signedKey]) {
        patternGroups[signedKey] = {
          transactions: [],
          category: transaction.category,
          categoryPath: transaction.categoryPath,
          groupName: transaction.groupName || transaction.categoryPath?.split('.')[0],
          bankInfo: transaction.bankInfo,
          patternKey: null,
          isPatternRule: false,
          positiveOnly: transaction.categoryPath && transaction.categoryPath.startsWith("RECEITA."),
          amount: amount
        };
      }
      
      patternGroups[signedKey].transactions.push(transaction);
    }
  });
  
  return { ...patternGroups, ...beneficiaryGroups, ...sicoobGroups, ...personalAccountGroups };
};

/**
 * Prepara os mapeamentos atualizados para salvar no Firestore
 * @param {Object} currentMappings - Mapeamentos atuais 
 * @param {Object} patternGroups - Grupos de padrões identificados
 * @returns {Object} - Mapeamentos atualizados
 */
export const prepareUpdatedMappings = (currentMappings, patternGroups) => {
  const updatedMappings = { ...currentMappings };
  
  Object.entries(patternGroups).forEach(([key, group]) => {
    // Tratar primeiro os grupos de contas pessoais
    if (group.isPersonalAccountPattern) {
      // Para contas pessoais, não precisamos salvar o mapeamento por ID
      // Todas as transações positivas serão tratadas pela função isPersonalAccount()
      return;
    }
    
    // Tratar grupos para contas empresariais com categorização automática desativada
    if (group.isBusinessDefaultPattern) {
      // Também não precisamos salvar o mapeamento por ID
      // Todas as transações positivas em contas empresariais serão tratadas
      // pela função isAutomaticBankCategorizationEnabled()
      return;
    }
    
    if (group.categoryPath && group.categoryPath.startsWith("RECEITA.") && 
        (key.startsWith("negative_") || (group.amount != null && group.amount < 0))) {
      console.log(`Ignorando grupo de transações negativas para categoria de RECEITA: ${key}`);
      return;
    }
    
    if (group.isSicoobPattern) { // Lidando com SICOOB (incluindo o PIX genérico)
      // A chave do Firebase será `sicoob_` + `sicoobKeyPartForGrouping`
      // `group.normalizedDescription` é a normalização completa para exibição
      // `group.sicoobKeyPartForGrouping` é a parte usada para a chave (pode ser genérica ou específica)

      const sicoobStorageKeyPart = group.sicoobKeyPartForGrouping || group.normalizedDescription; // Fallback
      const sicoobStorageKey = `sicoob_${sicoobStorageKeyPart}`;
      
      // Lógica para não sobrescrever mapeamentos de cartão SICOOB já existentes e mais genéricos
      if (
        (sicoobStorageKeyPart.includes("stone deb") || sicoobStorageKeyPart.includes("sub db")) &&
        !(sicoobStorageKeyPart === "cartao debito") && // Se não for já o genérico
        (updatedMappings["sicoob_cartao debito"]) // E o genérico já existe
      ) {
        console.log(`Usando mapeamento SICOOB genérico existente para cartão de débito ao invés de criar novo para: ${sicoobStorageKey}`);
        return;
      }
      if (
        (sicoobStorageKeyPart.includes("stone antec") || sicoobStorageKeyPart.includes("sub cr")) &&
        !(sicoobStorageKeyPart === "cartao credito") && // Se não for já o genérico
        (updatedMappings["sicoob_cartao credito"]) // E o genérico já existe
      ) {
        console.log(`Usando mapeamento SICOOB genérico existente para cartão de crédito ao invés de criar novo para: ${sicoobStorageKey}`);
        return;
      }

      // Se for um PIX genérico, e já existe um mapeamento para "sicoob_recebimento pix generico sicoob",
      // não criar um novo mapeamento com nome do pagador, a menos que seja uma despesa.
      if (group.isGenericSicoobPixReceipt && updatedMappings["sicoob_recebimento pix generico sicoob"] && sicoobStorageKey !== "sicoob_recebimento pix generico sicoob") {
          // Se a intenção é agrupar e já existe o genérico, não criar um específico para este PIX de receita.
          // No entanto, se a transação original (group.normalizedDescription) fosse diferente, ainda assim salvaria.
          // Esta lógica precisa ser cuidadosa.
          // O agrupamento em groupTransactionsByPattern já deve ter usado a chave genérica.
          // Então, sicoobStorageKey já deve ser "sicoob_recebimento pix generico sicoob".
      }

      updatedMappings[sicoobStorageKey] = {
        categoryName: group.isGenericSicoobPixReceipt ? "Pix" : group.category,
        categoryPath: group.isGenericSicoobPixReceipt ? "RECEITA.Pix" : group.categoryPath,
        groupName: group.isGenericSicoobPixReceipt ? "RECEITA" : group.groupName,
        lastUsed: new Date(),
        isSpecificMapping: false, // Será false para os agrupados, true se for um mapeamento muito específico
        isSicoobPattern: true,
        normalizedDescription: group.normalizedDescription, // Descrição completa normalizada para exibição
        sicoobKeyPart: sicoobStorageKeyPart, // A parte efetivamente usada na chave (útil para debug)
        positiveOnly: group.positiveOnly || (group.isGenericSicoobPixReceipt ? true : false), // PIX genérico é receita
        negativeOnly: group.negativeOnly || false,
        sampleDescription: group.transactions[0].description, // Descrição original da primeira transação
        bankInfo: group.bankInfo ? { org: group.bankInfo.org } : null
      };
      return;
    }
    
    if (group.isBeneficiaryPattern && group.beneficiary) {
      const beneficiaryKey = createBeneficiaryKey(group.beneficiary);
      updatedMappings[beneficiaryKey] = {
        categoryName: group.category,
        categoryPath: group.categoryPath,
        groupName: group.groupName,
        lastUsed: new Date(),
        isSpecificMapping: false,
        isBeneficiaryPattern: true,
        beneficiary: group.beneficiary,
        positiveOnly: group.positiveOnly || false,
        negativeOnly: group.negativeOnly || false,
        sampleDescription: group.transactions[0].description,
        bankInfo: group.bankInfo ? { org: group.bankInfo.org } : null
      };
      return;
    }
    
    const isPositive = key.startsWith("positive_");
    const cleanKey = isPositive ? 
      key.replace("positive_", "") : 
      key.replace("negative_", "");
    
    const storeKey = group.isPatternRule ? 
      (group.originalPatternKey || cleanKey) : 
      cleanKey;
    
    if (group.isPatternRule) {
      updatedMappings[storeKey] = {
        categoryName: group.category,
        categoryPath: group.categoryPath,
        groupName: group.groupName,
        lastUsed: new Date(),
        isSpecificMapping: false,
        patternRule: true,
        sampleDescription: group.transactions[0].description,
        positiveOnly: group.categoryPath && group.categoryPath.startsWith("RECEITA."),
        bankInfo: group.bankInfo ? { org: group.bankInfo.org } : null
      };
    } else {
      updatedMappings[cleanKey] = {
        categoryName: group.category,
        categoryPath: group.categoryPath,
        groupName: group.groupName,
        lastUsed: new Date(),
        isSpecificMapping: false,
        positiveOnly: group.categoryPath && group.categoryPath.startsWith("RECEITA."),
        bankInfo: group.bankInfo ? { org: group.bankInfo.org } : null
      };
    }
  });
  
  return updatedMappings;
};

/**
 * Encontra transações que precisam ser auto-categorizadas mas não foram salvas
 * @param {Array} transactions - Lista de transações
 * @param {Object} categoryMappings - Mapeamentos de categorias existentes
 * @returns {Array} - Transações que precisam ser salvas
 */
export const findUnsavedAutoMappedTransactions = (transactions, categoryMappings) => {
  if (!transactions || !transactions.length) return [];
  
  const processedTransactionIds = new Set();
  
  return transactions.filter(transaction => {
    if (processedTransactionIds.has(transaction.id)) {
      return false;
    }
    
    // Verificar se estamos em uma conta pessoal e se é uma transação positiva
    if (isPersonalAccount() && transaction.amount > 0) {
      // Para contas pessoais, todas as transações positivas são auto-mapeadas
      // mas não precisamos salvá-las, pois a regra está no código
      return false;
    }
    
    // Verificar se estamos em uma conta empresarial com categorização automática desativada
    if (isBusinessAccount() && transaction.amount > 0 && !isAutomaticBankCategorizationEnabled()) {
      // Também não precisamos salvá-las, pois a regra está no código
      return false;
    }
    
    const bankName = transaction.bankInfo?.org || "";
    const amount = transaction.amount || 0;
    const descriptionUpperCase = transaction.description.toUpperCase();
    
    if (amount <= 0 && transaction.categoryPath && transaction.categoryPath.startsWith("RECEITA.")) {
      return false;
    }
    
    const isSicoob = 
      bankName.includes("SICOOB") || 
      bankName.includes("COOP DE CRED") ||
      descriptionUpperCase.includes("SICOOB") || 
      descriptionUpperCase.includes("SICREDI");
    
    if (isSicoob) {
      const normalizedDescForDisplay = normalizeSicoobDescription(transaction.description, bankName);
      let sicoobKeyPart = normalizedDescForDisplay;

      const isReceita = amount > 0;
      const isGenericPixReceipt = isReceita &&
                                (descriptionUpperCase.includes("RECEBIMENTO PIX-PIX CRED") || descriptionUpperCase.includes("RECEBIMENTO PIX")) &&
                                !['ALELO', 'IFOOD', 'BANCO VR', 'PLUXEE', 'SAFE2PAY'].some(kw => descriptionUpperCase.includes(kw));
      
      if (isGenericPixReceipt) {
        sicoobKeyPart = "recebimento pix generico sicoob";
      }
      
      const sicoobKeyToSave = `sicoob_${sicoobKeyPart}`;
      if (categoryMappings[sicoobKeyToSave]) { // Se já existe mapeamento (genérico ou específico)
        return false;
      }
      // Lógica para evitar duplicados com cartões genéricos
      if (!isGenericPixReceipt) { // Só aplicar para não-PIX genéricos aqui
          if ((normalizedDescForDisplay.includes("stone deb") || normalizedDescForDisplay.includes("sub db")) &&
              (categoryMappings["sicoob_cartao debito"])) {
            return false;
          }
          if ((normalizedDescForDisplay.includes("stone antec") || normalizedDescForDisplay.includes("sub cr")) &&
              (categoryMappings["sicoob_cartao credito"])) {
            return false;
          }
      }
    }
    
    const boletoInfo = extractBoletoInfo(transaction.description);
    if (boletoInfo && boletoInfo.beneficiary) {
      const beneficiaryKey = createBeneficiaryKey(boletoInfo.beneficiary);
      if (beneficiaryKey && categoryMappings[beneficiaryKey]) {
        return false;
      }
    }
    
    const normalizedDescription = transaction.description.trim().toLowerCase();
    const pattern = matchTransactionPattern(normalizedDescription, bankName, amount);
    
    const needsProcessing = pattern && !categoryMappings[pattern.patternKey];
    
    if (needsProcessing) {
      processedTransactionIds.add(transaction.id);
    }
    
    return needsProcessing;
  }).map(transaction => {
    const bankName = transaction.bankInfo?.org || "";
    const descriptionUpperCase = transaction.description.toUpperCase();
    const amount = transaction.amount || 0;

    const isSicoob = 
      bankName.includes("SICOOB") || 
      bankName.includes("COOP DE CRED") ||
      descriptionUpperCase.includes("SICOOB") || 
      descriptionUpperCase.includes("SICREDI");
    
    if (isSicoob) {
      const isReceita = amount > 0;
      const isGenericPixReceipt = isReceita &&
                                (descriptionUpperCase.includes("RECEBIMENTO PIX-PIX CRED") || descriptionUpperCase.includes("RECEBIMENTO PIX")) &&
                                !['ALELO', 'IFOOD', 'BANCO VR', 'PLUXEE', 'SAFE2PAY'].some(kw => descriptionUpperCase.includes(kw));
      
      // A categoria e caminho para PIX genérico já deve ter sido definida em groupTransactionsByPattern
      // ou pelas regras em bankCategorizationRules.js
      let categoryForSicoob = transaction.category;
      let categoryPathForSicoob = transaction.categoryPath;
      let groupNameForSicoob = transaction.groupName || transaction.categoryPath?.split('.')[0];

      if(isGenericPixReceipt && categoryPathForSicoob !== "RECEITA.Pix"){
        categoryForSicoob = "Pix";
        categoryPathForSicoob = "RECEITA.Pix";
        groupNameForSicoob = "RECEITA";
      }

      return {
        ...transaction,
        id: transaction.id,
        normalizedDescription: normalizeSicoobDescription(transaction.description, bankName), // Para exibição
        isSicoobPattern: true,
        isGenericSicoobPixReceipt: isGenericPixReceipt, // Adiciona flag
        category: categoryForSicoob,
        categoryPath: categoryPathForSicoob,
        groupName: groupNameForSicoob
      };
    }
    
    const boletoInfo = extractBoletoInfo(transaction.description);
    if (boletoInfo && boletoInfo.beneficiary) {
      return {
        ...transaction,
        id: transaction.id,
        beneficiary: boletoInfo.beneficiary,
        isBeneficiaryPattern: true
      };
    }
    
    const normalizedDescription = transaction.description.trim().toLowerCase();
    const pattern = matchTransactionPattern(
      normalizedDescription,
      bankName,
      transaction.amount || 0
    );
    
    if (pattern) {
      return {
        ...transaction,
        id: transaction.id,
        category: pattern.category,
        categoryPath: pattern.categoryPath,
        groupName: pattern.categoryPath?.split('.')[0]
      };
    }
    
    return transaction;
  });
};